import React, { useState } from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import Button from '../../common/Button';
import { amountOverApproved, latestApproval, METHOD_LABELS } from './approvals';

// The customer approvals recorded on this estimate or invoice, the warning
// when the total has grown past the last one, and an inline form to record
// another. Inline, not a nested Modal: the invoice form is already a modal.
//
// "When" is the shop's local wall-clock time, sent as typed. Cleared, it is
// sent as now on the shop's clock: sent as null, the database filled in its own
// NOW(), which is UTC and hours off on the printed approval.

const localNow = () =>
  new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const inputClass =
  'w-full border border-border-strong rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent';

const emptyForm = (total) => ({
  method: 'in_person',
  authorized_by_name: '',
  contact_phone: '',
  authorized_at: localNow(),
  authorized_amount: Number(total || 0).toFixed(2),
  notes: '',
});

const ApprovalsPanel = ({ authorizations, total, recordApproval }) => {
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const latest = latestApproval(authorizations);
  const over = amountOverApproved(total, authorizations);
  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const validate = () => {
    if (!form.authorized_by_name.trim()) return 'Enter who approved.';
    if (form.method === 'phone' && !form.contact_phone.trim()) {
      return 'A phone approval needs the number that was called.';
    }
    const amount = Number(form.authorized_amount);
    if (form.authorized_amount === '' || Number.isNaN(amount) || amount < 0) return 'Enter the amount approved.';
    if (form.authorized_at && form.authorized_at > localNow()) return "The approval time can't be in the future.";
    return '';
  };

  const submit = async () => {
    const problem = validate();
    setError(problem);
    if (problem) return;
    setSaving(true);
    const ok = await recordApproval({
      method: form.method,
      authorized_by_name: form.authorized_by_name.trim(),
      contact_phone: form.contact_phone.trim() || null,
      authorized_amount: Number(form.authorized_amount),
      authorized_at: form.authorized_at || localNow(),
      notes: form.notes.trim() || null,
    });
    setSaving(false);
    if (ok) setForm(null);
  };

  return (
    <div className="mb-6">
      <h4 className="font-semibold text-ink mb-3">Customer Approval</h4>

      {over > 0 && (
        <div role="alert" className="mb-3 bg-warning-tint rounded-lg p-3 border border-warning-border flex items-start text-sm text-ink">
          <AlertTriangle className="h-4 w-4 text-warning mr-2 mt-0.5 flex-shrink-0" />
          <span>
            This total is ${over.toFixed(2)} more than the customer approved
            (${Number(latest.authorized_amount).toFixed(2)}). Record an additional approval before doing the extra work.
          </span>
        </div>
      )}

      {(authorizations || []).length === 0 ? (
        <p className="text-sm text-ink-muted mb-3">No approval recorded yet.</p>
      ) : (
        <ul className="mb-3 space-y-1 text-sm text-ink">
          {authorizations.map((a) => (
            <li key={a.id} className="flex items-start">
              <CheckCircle className="h-4 w-4 text-success mr-2 mt-0.5 flex-shrink-0" />
              <span>
                {METHOD_LABELS[a.method] || a.method} · {a.authorized_by_name}
                {a.contact_phone ? ` (${a.contact_phone})` : ''} · ${Number(a.authorized_amount).toFixed(2)} ·{' '}
                {new Date(a.authorized_at).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}

      {form === null ? (
        <Button variant="secondary" size="sm" onClick={() => { setError(''); setForm(emptyForm(total)); }}>
          Record approval
        </Button>
      ) : (
        <div className="bg-surface-page rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block text-sm font-medium text-ink">
              <span className="block mb-1">Method</span>
              <select aria-label="Method" value={form.method} onChange={set('method')} className={inputClass}>
                <option value="in_person">In person</option>
                <option value="phone">Phone</option>
                <option value="signed">Signed</option>
              </select>
            </label>
            <label className="block text-sm font-medium text-ink">
              <span className="block mb-1">Approved by</span>
              <input aria-label="Approved by" type="text" value={form.authorized_by_name} onChange={set('authorized_by_name')} className={inputClass} />
            </label>
            <label className="block text-sm font-medium text-ink">
              <span className="block mb-1">Phone</span>
              <input aria-label="Phone" type="tel" value={form.contact_phone} onChange={set('contact_phone')} className={inputClass} />
            </label>
            <label className="block text-sm font-medium text-ink">
              <span className="block mb-1">When</span>
              <input aria-label="When" type="datetime-local" value={form.authorized_at} max={localNow()} onChange={set('authorized_at')} className={inputClass} />
            </label>
            <label className="block text-sm font-medium text-ink">
              <span className="block mb-1">Amount</span>
              <input aria-label="Amount" type="number" min="0" step="0.01" value={form.authorized_amount} onChange={set('authorized_amount')} className={inputClass} />
            </label>
            <label className="block text-sm font-medium text-ink sm:col-span-2">
              <span className="block mb-1">Notes</span>
              <textarea aria-label="Notes" rows={2} value={form.notes} onChange={set('notes')} className={inputClass} />
            </label>
          </div>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setForm(null); setError(''); }}>Cancel</Button>
            <Button size="sm" loading={saving} onClick={submit}>Save approval</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalsPanel;
